/**
 * AI CONTEXT
 * Purpose: classes CRUD/search table.
 * Key deps: useClassTableData, AddClassFormModal, class table columns.
 * Referenced by: App route ROUTES.CLASSES.
 * Importance: ties together students, teachers, curriculum, sessions, attendance.
 * Refactor: remove unused canCreate or enforce it; restore consistent error handling.
 */
import { useMemo, useState } from "react"

import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import { buildTableColumns } from "#blocks/Table/buildTableColumns"
import Table2 from "#blocks/Table/Table2"
import TableFiltersRow from "#blocks/Table/TableFiltersRow"
import {
  CLASS_SORTABLE_FIELDS,
  CLASS_TABLE_COLUMNS,
  getClassSortValue,
} from "#constants/classOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type ClassForTable } from "#customTypes/composedTypes/classTypes"
import { SETTINGS_KEYS, Uuid } from "#customTypes/globalTypes"
import AddClassFormModal from "#forms/AddClass/AddClassFormModal"
import useClassTableData from "#hooks/dataHooks/useClassTableData"
import useHasPermission, {
  useResourcePermissions,
} from "#hooks/newHooks/useHasPermission"

const ClassesTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedClassId, setSelectedClassId] = useState<Uuid | undefined>()

  const {
    classesForTable,
    isReady,
    onSearch,
    sortField,
    sortOrder,
    onSortChange,
  } = useClassTableData()

  const { canRead } = useResourcePermissions(RESOURCES.CLASS)
  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  const columns = useMemo(
    () =>
      buildTableColumns<ClassForTable>(CLASS_TABLE_COLUMNS, {
        sortableFields: CLASS_SORTABLE_FIELDS,
        rightAlignFields: ["startTime", "endTime"],
        getSortValue: getClassSortValue,
        showId: canViewId,
      }),
    [canViewId]
  )

  const handleOpen = () => {
    if (!canRead) {
      return
    }

    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedClassId(undefined)
  }

  const handleRowClick = (selectedClassId: Uuid): void => {
    setSelectedClassId(selectedClassId)
    handleOpen()
  }

  if (!canRead) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex col style={{ padding: 20, width: "100%", maxWidth: 2200 }} gap={12}>
        <h1>Classes</h1>

        <TableFiltersRow
          onAdd={handleOpen}
          onSearch={onSearch}
          searchPlaceholder="Search by name, curriculum, teacher, skill, room"
          settingsKey={SETTINGS_KEYS.CLASSES}
        />

        {isOpen && canRead && (
          <AddClassFormModal onClose={handleClose} classId={selectedClassId} />
        )}
        <Table2<ClassForTable>
          isLoading={!isReady}
          data={classesForTable}
          columns={columns}
          onRowClick={(id) => {
            void handleRowClick(id)
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      </Flex>
    </Page>
  )
}

export default ClassesTable
